import React, { useState, useEffect } from 'react';

/**
 * Animated Shippy packing loader — shows the mascot going through packing steps
 * with animated boxes and progress indicators.
 */

const STEPS = [
  { icon: '📐', text: 'Measuring the boxes...', phase: 'measure' },
  { icon: '🧮', text: 'Calculating optimal packing...', phase: 'calculate' },
  { icon: '🔍', text: 'Finding the best strategy...', phase: 'strategy' },
  { icon: '🚚', text: 'Analyzing shipping costs...', phase: 'shipping' },
  { icon: '✨', text: 'Finalizing solution...', phase: 'finalize' },
];

export default function ShippyPackingLoader() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => {
      setStep(s => (s < STEPS.length - 1 ? s + 1 : s));
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const current = STEPS[step];

  return (
    <div className="flex flex-col items-center py-10 gap-5 select-none">
      {/* Shippy mascot with animated boxes */}
      <div className="relative" style={{ width: 180, height: 140 }}>
        {/* Small boxes floating around */}
        <svg className="absolute" style={{ left: 10, top: 8 }} width="32" height="32" viewBox="0 0 32 32">
          <g opacity="0.7">
            <animateTransform attributeName="transform" type="translate"
              values="0,0;-4,-6;0,0" dur="2s" repeatCount="indefinite" />
            <rect x="4" y="8" width="24" height="18" rx="2" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="1.5"/>
            <line x1="4" y1="14" x2="28" y2="14" stroke="#3B82F6" strokeWidth="1" opacity="0.5"/>
            <line x1="16" y1="8" x2="16" y2="26" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
          </g>
        </svg>

        <svg className="absolute" style={{ right: 8, top: 4 }} width="28" height="28" viewBox="0 0 32 32">
          <g opacity="0.5">
            <animateTransform attributeName="transform" type="translate"
              values="0,0;5,-8;0,0" dur="2.5s" repeatCount="indefinite" />
            <rect x="6" y="10" width="20" height="14" rx="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1.5"/>
            <line x1="16" y1="10" x2="16" y2="24" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
          </g>
        </svg>

        <svg className="absolute" style={{ left: 4, bottom: 8 }} width="24" height="24" viewBox="0 0 32 32">
          <g opacity="0.4">
            <animateTransform attributeName="transform" type="translate"
              values="0,0;-6,2;0,0" dur="3s" repeatCount="indefinite" />
            <rect x="6" y="10" width="20" height="14" rx="2" fill="#E0E7FF" stroke="#6366F1" strokeWidth="1.5"/>
            <line x1="16" y1="10" x2="16" y2="24" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
          </g>
        </svg>

        {/* Main Shippy mascot — center */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          width={100}
          height={100}
          style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <defs>
            <linearGradient id="spl-top" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#93C5FD" />
              <stop offset="100%" stopColor="#60A5FA" />
            </linearGradient>
            <linearGradient id="spl-front" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
            <linearGradient id="spl-side" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60A5FA" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
          </defs>

          {/* Left leg */}
          <rect x="30" y="72" width="8" height="16" rx="4" fill="#1E40AF">
            <animateTransform attributeName="transform" type="rotate" values="-8,34,72;8,34,72;-8,34,72" dur="0.5s" repeatCount="indefinite" />
          </rect>
          <ellipse cx="34" cy="90" rx="8" ry="4" fill="#1D4ED8">
            <animateTransform attributeName="transform" type="rotate" values="-8,34,72;8,34,72;-8,34,72" dur="0.5s" repeatCount="indefinite" />
          </ellipse>

          {/* Right leg */}
          <rect x="54" y="72" width="8" height="16" rx="4" fill="#1E40AF">
            <animateTransform attributeName="transform" type="rotate" values="8,58,72;-8,58,72;8,58,72" dur="0.5s" repeatCount="indefinite" />
          </rect>
          <ellipse cx="58" cy="90" rx="8" ry="4" fill="#1D4ED8">
            <animateTransform attributeName="transform" type="rotate" values="8,58,72;-8,58,72;8,58,72" dur="0.5s" repeatCount="indefinite" />
          </ellipse>

          {/* Body — bounces */}
          <g>
            <animateTransform attributeName="transform" type="translate" values="0,0;0,-4;0,0" dur="0.5s" repeatCount="indefinite" />

            {/* Left arm — holding a small box */}
            <g>
              <animateTransform attributeName="transform" type="rotate" values="0,20,48;-15,20,48;0,20,48" dur="0.8s" repeatCount="indefinite" />
              <path d="M20 48 Q10 38, 8 26 Q7 20, 12 18" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
              {/* Small box in hand */}
              <rect x="2" y="6" width="16" height="13" rx="2" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="1.5">
                <animateTransform attributeName="transform" type="rotate" values="-3,10,12;3,10,12;-3,10,12" dur="0.8s" repeatCount="indefinite" />
              </rect>
              <line x1="10" y1="6" x2="10" y2="19" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round" opacity="0.7">
                <animateTransform attributeName="transform" type="rotate" values="-3,10,12;3,10,12;-3,10,12" dur="0.8s" repeatCount="indefinite" />
              </line>
            </g>

            {/* Right arm */}
            <g>
              <animateTransform attributeName="transform" type="rotate" values="0,72,48;15,72,48;0,72,48" dur="0.6s" repeatCount="indefinite" />
              <path d="M72 48 Q82 52, 80 62" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
              <circle cx="80" cy="63" r="5" fill="#FCD34D" />
            </g>

            {/* Box body */}
            <path d="M22 30 L46 18 L76 30 L52 42 Z" fill="url(#spl-top)" stroke="#1E40AF" strokeWidth="1.5" />
            <line x1="46" y1="18" x2="52" y2="42" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
            <path d="M22 30 L52 42 L52 75 L22 63 Z" fill="url(#spl-front)" stroke="#1E40AF" strokeWidth="1.5" />
            <path d="M76 30 L52 42 L52 75 L76 63 Z" fill="url(#spl-side)" stroke="#1E40AF" strokeWidth="1.5" />
            <line x1="37" y1="36" x2="37" y2="69" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />

            {/* Eyes — focused/working look */}
            <ellipse cx="33" cy="48" rx="3.5" ry="4" fill="white" />
            <ellipse cx="45" cy="46" rx="3.5" ry="4" fill="white" />
            <ellipse cx="34.5" cy="47.5" rx="2" ry="2.5" fill="#1E293B" />
            <ellipse cx="46.5" cy="45.5" rx="2" ry="2.5" fill="#1E293B" />
            <circle cx="35.5" cy="46" r="1" fill="white" />
            <circle cx="47.5" cy="44" r="1" fill="white" />

            {/* Smile */}
            <path d="M30 54 Q38 62, 48 54" fill="none" stroke="#1E293B" strokeWidth="2.5" strokeLinecap="round" />

            {/* Cheeks */}
            <ellipse cx="28" cy="55" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
            <ellipse cx="50" cy="53" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
          </g>
        </svg>
      </div>

      {/* Step progress */}
      <div className="flex flex-col items-center gap-3 w-full max-w-xs">
        {/* Current step text */}
        <div className="flex items-center gap-2 text-sm font-medium text-foreground animate-pulse">
          <span className="text-lg">{current.icon}</span>
          <span>{current.text}</span>
        </div>

        {/* Progress dots */}
        <div className="flex gap-2">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i <= step
                  ? 'bg-blue-500 w-6'
                  : 'bg-muted w-3'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
